import React, { useEffect, useState, useMemo, useContext } from "react";
import styles from "./Dashboard.module.scss";
import _ from "lodash";
import gql from "graphql-tag";
import { formatAs, getCurrentQuarter } from "../../utils/dates";
import { useDepartmentFilter } from "../../context/departmentFilterContext";
import { useMutation, useQuery } from "@apollo/client";
import { SnackbarContext } from "../../context/snackbarContext";
import { indexToLetter, toPascalCase } from "../../utils/misc";
import useCorpPlan from "../../hooks/useCorpPlan";
import { diffInDays } from "../../utils/dates";
import { KANBAN_ITEM_FIELDS } from "../../utils/fragments";
import { useAuth } from "../../context/authContext";
import { isAuthed } from "../../utils/authorization";
import { DialogContext } from "../../context/dialogContext";
import { FetchContext } from "../../context/fetchContext";

import { Chip, Typography, MenuItem, useTheme, IconButton, Collapse } from "@material-ui/core";
import Icon from "@mdi/react";
import {
  mdiRhombusOutline,
  mdiRhombusSplitOutline,
  mdiCheckboxMarkedOutline,
  mdiAlertDecagramOutline,
  mdiCalendarAlert,
  mdiPencil,
  mdiTextShort,
  mdiText,
} from "@mdi/js";

import KanbanBoard from "../../components/KanbanBoard/KanbanBoard";
import UserAvatars from "../../components/UserAvatars/UserAvatars";
import Menu from "../../components/Menu/Menu";
import ConfirmDeletionDialog from "../../components/ConfirmDeletionDialog/ConfirmDeletionDialog";

const COLUMNS = [
  { id: "on hold", name: "On Hold" },
  { id: "not started", name: "Not Started" },
  { id: "in progress", name: "In Progress" },
  { id: "roadblocked", name: "Roadblocked" },
  { id: "completed", name: "Completed" },
];

const Kanban = ({ org, filterUser, filterType }) => {
  const theme = useTheme();

  const { fetch, requestFetch } = useContext(FetchContext);
  const { dialog, setDialog } = useContext(DialogContext);
  const { snack } = useContext(SnackbarContext);

  const { auth } = useAuth();
  const canEdit = isAuthed(auth, "department facilitator");

  const fiscalYear = org.fiscalYear;
  const { corpForSelectedYear } = useCorpPlan({ orgId: org.id, fiscalYear }); // is the current year one year corp plan by default
  const { departmentFilter } = useDepartmentFilter();

  const [expandedTodos, setExpandedTodos] = useState({});

  // const [confirmDelete, setConfirmDelete] = useState(false);
  // const [itemToDelete, setItemToDelete] = useState();

  const filters = useMemo(() => {
    return {
      organization: org.id,
      users: filterUser ? [filterUser.id] : [],
      sharedPlanId: departmentFilter.sharedPlanId,
      oneYearCorpPlan: corpForSelectedYear.id,
      type: filterType,
    };
  }, [org, filterUser, filterType, departmentFilter, corpForSelectedYear]);

  // const [deleteTodo, { loading: deleteTodoLoading }] = useMutation(DELETE_TODO);
  // const [deleteRock, { loading: deleteRockLoading }] = useMutation(DELETE_ROCK);
  // const [deleteSc, { loading: deleteScLoading }] = useMutation(DELETE_SUCCESS_CRITERIA);

  const toggleTodoDesc = (todoId) => {
    setExpandedTodos((prev) => ({ ...prev, [todoId]: !_.get(prev, todoId, false) }));
  };

  const getHeader = (item) => {
    const { type, kanbanStatus, targetDate } = item;

    if (type !== "successCriteria" || kanbanStatus === "completed" || _.isEmpty(targetDate)) return {};

    const daysRemaining = targetDate ? diffInDays(targetDate, new Date()) : null;
    const isOverdue = daysRemaining < 0;
    const daysCount = Math.abs(daysRemaining);

    let bgColor,
      dateStr = `${daysCount} Day${daysCount === 1 ? "" : "s"} ${isOverdue ? "Overdue" : "Remaining"}`;

    if (daysRemaining <= 5) {
      bgColor = "#B22222"; // firebrick
    } else if (daysRemaining <= 30) {
      bgColor = "#FFD700"; // gold
    } else if (daysRemaining <= 60) {
      bgColor = "#4682B4"; // steelblue
    } else {
      bgColor = theme.palette.grey[300];
      dateStr = formatAs(targetDate, "yyyy-MM-dd");
    }

    const color = theme.palette.getContrastText(bgColor);

    return {
      textColor: color,
      header: (
        <div
          className={styles.cardHeader}
          style={{ backgroundColor: bgColor, color, gap: theme.spacing(1), borderColor: theme.palette.grey[300] }}
        >
          <Icon path={mdiCalendarAlert} size={0.75} color={color} />
          <Typography variant="subtitle2" style={{ color }}>
            {dateStr}
          </Typography>
        </div>
      ),
    };
  };

  const getSubValue = (item) => {
    let subValue;
    switch (item.type) {
      case "issue":
      case "todo":
        subValue = _.get(item, "referenceDoc.value", "No tie-in");
        break;
      case "rock":
        subValue = _.get(item, "objective.value", "No objective tie-in");
        break;
      case "successCriteria":
        subValue = _.get(item, "rock.value");
        break;
      default:
    }

    return <Typography variant="caption">{subValue}</Typography>;
  };

  const getDisplayIdPill = (item) => {
    const { type, orgNumber } = item;

    let chipLabel,
      classNameSuffix = _.startCase(type);
    switch (type) {
      case "issue":
        chipLabel = `ISSUE I${orgNumber}`;
        break;
      case "todo":
        chipLabel = `TODO T${orgNumber}`;
        break;
      case "rock":
        chipLabel = `ROCK R${orgNumber}`;
        break;
      case "successCriteria":
        const { rock, orderIdx } = item;
        const rockOrgNumber = _.get(rock, "orgNumber");

        chipLabel = `SC R${rockOrgNumber}${indexToLetter(parseInt(orderIdx))}`;
        classNameSuffix = "SC";
        break;
      default:
    }

    return <Chip label={chipLabel} size="small" className={_.get(styles, `itemLabel${classNameSuffix}`)} style={{ borderRadius: 16 }} />;
  };

  const getPriorityPill = (priority) => {
    if (_.isEmpty(priority)) return null;

    const classNameSuffix = _.get(
      {
        high: "HighP",
        medium: "MedP",
        low: "LowP",
      },
      priority,
      ""
    );

    return (
      <Chip
        label={_.toUpper(priority)}
        size="small"
        className={_.get(styles, `itemLabel${classNameSuffix}`)}
        style={{ borderRadius: 16 }}
      />
    );
  };

  const getPlanPill = (item) => {
    const plan = item.type === "successCriteria" ? _.get(item, "rock.plan") : _.get(item, "plan");
    if (_.isEmpty(plan) || _.every(_.values(_.omit(plan, "__typename")), _.isEmpty)) {
      return null;
    }

    const labelBackup = plan.departmentName.slice(0, 4);
    const label = _.toUpper(plan.shortName ?? labelBackup);
    const color = plan.color || theme.palette.secondary.main;

    return <Chip label={label} style={{ backgroundColor: color, color: theme.palette.getContrastText(color) }} size="small" />;
  };

  const getItemType = (item = {}) => {
    const { __typename, category } = item;
    const type = __typename === "Todo" ? category : __typename;

    return _.camelCase(type);
  };

  const handleEditItem = (item) => {
    const corpPlan = _.get(item, "plan.plan.id", _.get(item, "plan.id")) || corpForSelectedYear.id;

    let dialogCtx = {};
    switch (item.type) {
      case "issue":
      case "todo":
        dialogCtx = { addTodoDialog: { open: true, todo: item, planId: corpPlan } };
        break;
      case "rock":
        dialogCtx = { addRockDialog: { open: true, rock: item, planId: corpPlan } };
        break;
      case "successCriteria":
        dialogCtx = { addSuccessCriteriaDialog: { open: true, successCriteria: item, planId: corpPlan, rock: _.get(item, "rock.id") } };
        break;
      default:
    }

    setDialog((prev) => ({ ...prev, ...dialogCtx }));
  };

  // const handleConfirmDelete = (open, item) => {
  //   setConfirmDelete(open);
  //   setItemToDelete(item);
  // };

  // const handleDeleteItem = async () => {
  //   const { id, value, type } = itemToDelete;

  //   try {
  //     let deleteMutation;
  //     switch (type) {
  //       case "issue":
  //       case "todo":
  //         deleteMutation = deleteTodo;
  //         break;
  //       case "rock":
  //         deleteMutation = deleteRock;
  //         break;
  //       case "successCriteria":
  //         deleteMutation = deleteSc;
  //         break;
  //       default:
  //     }

  //     const res = await deleteMutation({ variables: { id } });
  //     const delMutationData = `delete${toPascalCase(type)}`;

  //     if (res.data[delMutationData]) {
  //       snack(`Deleted "${value}"`);
  //       handleConfirmDelete(false);
  //       requestFetch();
  //     }
  //   } catch (err) {
  //     snack(`Failed to delete ${type}`, "error");
  //   }
  // };

  // const deleteLoading = deleteTodoLoading || deleteRockLoading || deleteScLoading;

  return (
    <>
      <div
        style={{
          height: `calc(100% - ${theme.spacing(2)}px)`,
          overflowX: "auto",
          overflowY: "hidden",
          margin: `${theme.spacing(2)}px ${theme.spacing(3)}px 0`,
          paddingBottom: theme.spacing(3),
        }}
      >
        <KanbanBoard
          columns={COLUMNS}
          filters={filters}
          columnDataQuery={GET_KANBAN_STATUS_DATA}
          updateItemMutation={MOVE_KANBAN_CARD}
          updateItemMutationVarDict={MOVE_KANBAN_CARD_VARS}
          queryName="kanbanStatusData"
          mutationName="moveKanbanCard"
          getItemType={getItemType}
          renderCardContents={({ item, dragHandle }) => {
            const { id, type, value, description, kanbanPosition, priority, user, users } = item;
            const { textColor = "inherit", header } = getHeader(item);

            const isTodoIssue = ["todo", "issue"].includes(type);
            const showTodoDesc = _.get(expandedTodos, id, false);

            return (
              <>
                {header}
                <div className={styles.cardTools} style={{ color: textColor }}>
                  {isTodoIssue && (
                    <IconButton
                      title={`${showTodoDesc ? "Hide" : "Show"} description`}
                      onClick={() => toggleTodoDesc(id)}
                      size="small"
                      style={{ color: textColor }}
                    >
                      <Icon path={showTodoDesc ? mdiText : mdiTextShort} size={0.65} />
                    </IconButton>
                  )}
                  {canEdit && (
                    // <Menu
                    //   menuProps={{
                    //     anchorOrigin: {
                    //       vertical: "center",
                    //       horizontal: "right",
                    //     },
                    //     transformOrigin: {
                    //       vertical: "center",
                    //       horizontal: "left",
                    //     },
                    //   }}
                    // >
                    //   <MenuItem onClick={() => handleEditItem(item)}>Edit</MenuItem>
                    //   <MenuItem onClick={() => handleConfirmDelete(true, item)} className={styles.delete}>
                    //     Delete
                    //   </MenuItem>
                    // </Menu>
                    <IconButton title="Edit" onClick={() => handleEditItem(item)} size="small" style={{ color: textColor }}>
                      <Icon path={mdiPencil} size={0.65} />
                    </IconButton>
                  )}
                  {dragHandle}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing(1), padding: theme.spacing(1.5) }}>
                  {/* <span>{id}</span> */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      {getSubValue(item)}
                      <Typography>
                        <strong>{value}</strong>
                      </Typography>
                      <Collapse in={showTodoDesc}>
                        <Typography variant="caption" style={{ whiteSpace: "pre-wrap" }}>
                          {_.isEmpty(description) ? `No ${type} description` : description}
                        </Typography>
                      </Collapse>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: theme.spacing(0.5), marginTop: theme.spacing(1) }}>
                        {getPlanPill(item)}
                        {getDisplayIdPill(item)}
                        {getPriorityPill(priority)}
                        {/* <Chip label={kanbanPosition} size="small" style={{ backgroundColor: "orange" }} /> */}
                      </div>
                    </div>
                    <div style={{ alignSelf: "flex-end", alignContent: "space-between" }}>
                      <UserAvatars users={users ? users : user ? [user] : undefined} />
                    </div>
                  </div>
                </div>
              </>
            );
          }}
        />
      </div>
      {/* <ConfirmDeletionDialog
        itemType={_.lowerCase(_.get(itemToDelete, "type"))}
        value={_.get(itemToDelete, "value")}
        confirmOpen={confirmDelete}
        handleConfirmOpen={handleConfirmDelete}
        handleDeletion={handleDeleteItem}
        deleteLoading={deleteLoading}
      /> */}
    </>
  );
};

export default Kanban;

const MOVE_KANBAN_CARD = gql`
  ${KANBAN_ITEM_FIELDS}
  mutation moveKanbanCardMutation($id: ID!, $type: String!, $kanbanStatus: String, $targetPosition: Int!) {
    moveKanbanCard(id: $id, type: $type, kanbanStatus: $kanbanStatus, targetPosition: $targetPosition) {
      ...KanbanItemFields
    }
  }
`;

const MOVE_KANBAN_CARD_VARS = {
  kanbanPosition: "targetPosition",
};

const GET_KANBAN_STATUS_DATA = gql`
  ${KANBAN_ITEM_FIELDS}
  query kanbanStatusDataQuery(
    $organization: ID!
    $users: [ID!]
    $kanbanStatus: String!
    $type: String
    $oneYearCorpPlan: ID
    $sharedPlanId: ID
    $cursor: Int
  ) {
    kanbanStatusData(
      organization: $organization
      users: $users
      kanbanStatus: $kanbanStatus
      type: $type
      oneYearCorpPlan: $oneYearCorpPlan
      sharedPlanId: $sharedPlanId
      cursor: $cursor
    ) {
      items {
        ...KanbanItemFields
      }
      nextCursor
      hasMore
      totalItems
    }
  }
`;

// const DELETE_TODO = gql`
//   mutation ($id: ID!) {
//     deleteTodo(id: $id) {
//       todo {
//         id: _id
//       }
//       successCriteria {
//         id
//         todos {
//           id: _id
//         }
//       }
//       rock {
//         id
//         todos {
//           id: _id
//         }
//       }
//       metric {
//         id
//         todos {
//           id: _id
//         }
//       }
//       objective {
//         id
//         todos {
//           id: _id
//         }
//       }
//     }
//   }
// `;

// const DELETE_ROCK = gql`
//   mutation ($id: ID!) {
//     deleteRock(id: $id) {
//       deletedRock {
//         id
//       }
//       deletedSuccessCriterias {
//         id
//       }
//       updatedObjective {
//         id
//         rocks {
//           id
//         }
//       }
//       updatedPlan {
//         id
//         rocks {
//           id
//         }
//       }
//     }
//   }
// `;

// const DELETE_SUCCESS_CRITERIA = gql`
//   mutation ($id: ID!) {
//     deleteSuccessCriteria(id: $id) {
//       deletedSuccessCriteria {
//         id
//       }
//       updatedRock {
//         id
//         successCriterias {
//           id
//         }
//       }
//     }
//   }
// `;
